import { CollapsableCard } from '@app/app/_components/CollapsableCard/CollapsableCard';
import { useLocale, useTranslations } from 'next-intl';
import { useState, useCallback } from 'react';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseHomeInsuranceResult } from '@app/service/eshop/home/actions/purchaseHomeInsurance';
import { HomeInsuranceRequestResult } from '@app/service/eshop/home/actions/requestHomeInsurance';
import { ProductDataObject } from '@app/domain/model/eshop/home/ProductDataObject';
import { ProductDataPurpose } from '@app/domain/model/eshop/transport/ProductDataPurpose';
import { ProductDataLeasing } from '@app/domain/model/eshop/transport/ProductDataLeasing';
import { ProductDataRider } from '@app/domain/model/eshop/transport/ProductDataRider';
import { HomeInsuranceRiskPackage } from '@app/domain/model/eshop/home/HomeInsuranceRiskPackage';
import { PolicyholderFormFields } from '@app/app/esales/home/_sections/HomeInsuranceWizardSection/hooks/usePolicyholderFormFields';
import { Tenant } from '@app/infrastructure/tenant/model/Tenant';
import { PropertyInformationPanel } from '@app/app/esales/home/_sections/steps/SummaryStep/PropertyInformationPanel';
import { PledgedToBankPanel } from '@app/app/esales/home/_sections/steps/SummaryStep/PledgedToBankPanel';
import { PolicyholderSummaryCard } from '@app/app/esales/home/_sections/steps/SummaryStep/PolicyholderSummaryCard';
import { InsuranceConditionsPanel } from '@app/app/esales/home/_sections/steps/SummaryStep/InsuranceConditionsPanel';
import { PaymentFrequencyPanel } from '@app/app/esales/home/_sections/steps/SummaryStep/PaymentFrequencyPanel';
import { InsuranceStartDatePanel } from '@app/app/esales/home/_sections/steps/SummaryStep/InsuranceStartDatePanel';
import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { SummaryEditButton } from '@app/app/esales/transport/components/SummaryEditButton';
import { UpdateModal } from '@app/app/esales/transport/components/UpdateModal';
import { Heading } from '@app/app/_components/Heading/Heading';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { CheckboxInput } from '@app/app/_components/Input/CheckboxInput';
import Link from 'next/link';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { ExpandableText } from '@app/app/_components/ExpandableText/ExpandableText';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';

interface Props {
    formState: FormState<PurchaseHomeInsuranceResult>;
    requestResult: Nullable<HomeInsuranceRequestResult>;
    policyholderFields: PolicyholderFormFields;
    personalCode: string;
    tenant: Tenant;
    onSavePolicyholder: (data: PolicyholderFormFields) => void;
    objects: ProductDataObject[];
    purposes: Nullable<ProductDataPurpose>;
    leasings: ProductDataLeasing[];
    selectedRisk: Nullable<HomeInsuranceRiskPackage>;
    totalPrice: number;
    priceToPayNow: number;
    paymentFrequency: number;
    onPaymentFrequencyChange: (paymentFrequency: number) => void;
    editObjectSummary: () => void;
    editPolicy: () => void;
    editBeginDate: () => void;
    editAdditionalRisks: () => void;
    selectedRiders: ProductDataRider[];
    handlePledgedToBankChange: (value: string) => void;
    pledgedToBank: Nullable<string>;
    selectedLeasing: Nullable<string>;
    handleSelectedLeasingChange: (value: string) => void;
}

export function SummaryStep({
    formState,
    requestResult,
    policyholderFields,
    personalCode,
    tenant,
    onSavePolicyholder,
    objects,
    purposes,
    leasings,
    selectedRisk,
    totalPrice,
    priceToPayNow,
    paymentFrequency,
    onPaymentFrequencyChange,
    editObjectSummary,
    editPolicy,
    editBeginDate,
    editAdditionalRisks,
    selectedRiders,
    handlePledgedToBankChange,
    pledgedToBank,
    selectedLeasing,
    handleSelectedLeasingChange,
}: Props) {
    const t = useTranslations();
    const parameters = useAppParameters();
    const locale = useLocale();
    const [ridersUpdateModalOpen, setRidersUpdateModalOpen] = useState(false);

    const handleOpenRidersUpdateModal = useCallback(() => {
        setRidersUpdateModalOpen(true);
    }, []);

    const handleCloseRidersUpdateModal = useCallback(() => {
        setRidersUpdateModalOpen(false);
    }, []);

    const handleProceedRidersEdit = useCallback(() => {
        handleCloseRidersUpdateModal();
        editAdditionalRisks();
    }, [handleCloseRidersUpdateModal, editAdditionalRisks]);

    if (requestResult == null) {
        return null;
    }

    return (
        <div className="flex flex-col gap-4">
            <CollapsableCard defaultCollapsed={false} label={t('eshop.home.summary.propertyInformation')}>
                <PropertyInformationPanel
                    requestResult={requestResult}
                    policyholderFields={policyholderFields}
                    formState={formState}
                    objects={objects}
                    purposes={purposes}
                    onEdit={editObjectSummary}
                />
                <PledgedToBankPanel
                    formState={formState}
                    leasings={leasings}
                    pledgedToBank={pledgedToBank}
                    handlePledgedToBankChange={handlePledgedToBankChange}
                    selectedLeasing={selectedLeasing}
                    handleSelectedLeasingChange={handleSelectedLeasingChange}
                />
            </CollapsableCard>

            <CollapsableCard defaultCollapsed={false} label={t('eshop.home.summary.policyholderInformation')}>
                <PolicyholderSummaryCard
                    policyholderFields={policyholderFields}
                    formState={formState}
                    personalCode={personalCode}
                    tenant={tenant}
                    onSave={onSavePolicyholder}
                />
            </CollapsableCard>

            {selectedRisk && (
                <CollapsableCard defaultCollapsed={false} label={t('eshop.home.summary.policy')}>
                    <InsuranceConditionsPanel selectedRisk={selectedRisk} totalPrice={totalPrice} onEdit={editPolicy} />
                    <PaymentFrequencyPanel
                        totalPrice={totalPrice}
                        priceToPayNow={priceToPayNow}
                        paymentFrequency={paymentFrequency}
                        onPaymentFrequencyChange={onPaymentFrequencyChange}
                    />
                    <InsuranceStartDatePanel
                        beginDate={formState.data?.formData.beginDate ?? ''}
                        onEdit={editBeginDate}
                    />
                    {selectedRiders.length > 0 && (
                        <>
                            <CollapsablePanel
                                label={t('eshop.home.additionalRisks')}
                                addon={
                                    <SummaryEditButton onClick={handleOpenRidersUpdateModal}>
                                        {t('general.edit')}
                                    </SummaryEditButton>
                                }
                                collapsable={false}>
                                <div className="px-6">
                                    {selectedRiders.map(rider => (
                                        <div
                                            className="border-b border-neutral-300 py-2 last:border-b-0"
                                            key={rider.riderGuid}>
                                            <Heading className="mb-4" level="h4">
                                                {rider.riderName}
                                            </Heading>
                                            <div className="text-md font-medium">
                                                <span>{formatEuroValue(rider.premiums.premium, tenant)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CollapsablePanel>
                            <UpdateModal
                                updateModalOpen={ridersUpdateModalOpen}
                                handleCloseUpdateModal={handleCloseRidersUpdateModal}
                                navigateToStep={handleProceedRidersEdit}
                            />
                        </>
                    )}
                </CollapsableCard>
            )}

            <div className="mt-6 flex flex-col gap-4">
                <CheckboxInput
                    name="acceptToPrivacyPolicy"
                    error={formState.violations?.acceptToPrivacyPolicy}
                    label={t.rich('eshop.home.summary.privacyPolicy', {
                        link: chunks => (
                            <Link
                                className="underline"
                                href={
                                    locale === 'lt'
                                        ? parameters.eshopFooterUrls.privacyPolicy
                                        : parameters.eshopFooterUrls.privacyPolicyEn
                                }
                                target="_blank">
                                {chunks}
                            </Link>
                        ),
                    })}
                />

                <CheckboxInput
                    name="acceptToHomeRules"
                    error={formState.violations?.acceptToHomeRules}
                    label={t.rich('eshop.home.summary.homeRulesConsentLabel', {
                        link: chunks => (
                            <Link
                                className="underline"
                                href={
                                    locale === 'lt'
                                        ? parameters.eshopHomeInsuranceInfoUrls.homeRulesLt
                                        : parameters.eshopHomeInsuranceInfoUrls.homeRulesEn
                                }
                                target="_blank">
                                {chunks}
                            </Link>
                        ),
                    })}
                />

                <ExpandableText className="text-gray-700 text-sm">
                    <div className="whitespace-pre-line">
                        {t.rich('eshop.personalAccidents.summaryStep.directMarketingInfoText', {
                            compensaEmail: chunks => (
                                <Link
                                    href="mailto:tiesioginerinkodara@compensa.lt"
                                    className="hover:text-blue-800 text-blue-600 underline"
                                    target="_blank">
                                    {chunks}
                                </Link>
                            ),
                            compensaLifeEmail: chunks => (
                                <Link
                                    href="mailto:tiesioginerinkodara@compensalife.lt"
                                    className="hover:text-blue-800 text-blue-600 underline"
                                    target="_blank">
                                    {chunks}
                                </Link>
                            ),
                            privacyPolicyLink: chunks => (
                                <Link
                                    href={
                                        locale === 'lt'
                                            ? parameters.eshopFooterUrls.privacyPolicy
                                            : parameters.eshopFooterUrls.privacyPolicyEn
                                    }
                                    className="hover:text-blue-800 text-blue-600 underline"
                                    target="_blank">
                                    {chunks}
                                </Link>
                            ),
                        })}
                    </div>
                </ExpandableText>

                <div className="flex flex-col gap-4">
                    <Paragraph isBold>{t('eshop.home.summaryStep.directMarketingAgreement')}</Paragraph>
                    <CheckboxInput
                        name="acceptToMarketingCnl"
                        label={t('eshop.home.summaryStep.agreeToMarketingCompensa')}
                    />
                    <CheckboxInput
                        name="acceptToMarketingCl"
                        label={t('eshop.home.summaryStep.agreeToMarketingCompensaLife')}
                    />
                </div>
            </div>
        </div>
    );
}
